import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { VIEWING_LABELS, VIEWING_STATUSES, type ViewingStatus } from '@wasity/shared';
import { Card, DateText, EmptyState, ErrorNotice, Input, PageHeader, Pagination, Select, Skeleton, Table, Toolbar } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, qs } from '@/lib/api';
import type { VE } from '@/lib/types';
import { VeChip, waitingOn } from './common';

const LIMIT = 50;

/**
 * The search box, held back a moment before it becomes a request.
 *
 * The search is the server's now — filtering the page already in the browser told an admin that a
 * record on the next page did not exist — so every keystroke would otherwise be a round trip.
 */
function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function AdminViewings() {
  const { t, pick, localTitle } = useI18n();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | ViewingStatus>('');
  const [offset, setOffset] = useState(0);
  const search = useDebounced(q);
  // A new search starts at the first page. Without this, typing while on page 3 searched from row 100.
  useEffect(() => setOffset(0), [search]);
  const list = useQuery({ queryKey: ['admin', 've', 'list', status, search, offset], queryFn: () => api.get<{ items: VE[] }>(`/api/admin/viewings${qs({ status, q: search, limit: LIMIT, offset })}`) });
  const rows = list.data?.items ?? [];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.viewings') }]} title={t('nav.viewings')} />
      <Toolbar end={list.data ? t('admin.viewings.totalN', { n: rows.length }) : ''}>
        <Input className="w-[260px] max-w-full" placeholder={t('app.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('app.search')} />
        <Select className="w-[200px]" value={status} onChange={(e) => { setStatus(e.target.value as '' | ViewingStatus); setOffset(0); }} aria-label={t('app.status')}>
          <option value="">{t('admin.requests.allStatuses')}</option>
          {VIEWING_STATUSES.map((s) => <option key={s} value={s}>{pick(VIEWING_LABELS[s])}</option>)}
        </Select>
      </Toolbar>
      {list.isLoading ? <Skeleton lines={8} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : rows.length ? (
        <Card className="p-0 overflow-hidden">
          <Table className="border-0 rounded-none">
            <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.buyer')}</th><th>{t('app.broker')}</th><th>{t('admin.viewings.proposedSlot')}</th><th>{t('admin.viewings.turnOn')}</th><th>{t('app.status')}</th></tr></thead>
            <tbody>
              {rows.map((v) => {
                const turn = waitingOn(v.status);
                return (
                  <tr key={v.id}>
                    <td className="num"><Link to={`/admin/viewings/${v.id}`}>{v.number}</Link></td>
                    <td>{localTitle(v)}</td>
                    <td>{v.buyerName ?? '—'}</td>
                    <td>{v.brokerName ?? '—'}</td>
                    <td><DateText at={v.confirmedSlotStart ?? v.slotStart} kind="slot" /></td>
                    <td>{turn ? t(`status.turn.${turn}`) : '—'}</td>
                    <td><VeChip ve={v} /></td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      ) : <EmptyState title={t('app.noResults')} />}
      <Pagination offset={offset} limit={LIMIT} count={list.data?.items.length ?? 0} onChange={setOffset} labels={{ prev: t('app.prev'), next: t('app.next'), page: t('app.page') }} />
    </div>
  );
}
